// /app/ajustes/plan-de-cuentas — administración del árbol contable.
//
// Sprint 1 (2026-05-16): nueva página. Lista el árbol con indentación,
// permite agregar sub-cuentas debajo de cualquier cuenta (cliente), renombrar
// (sistema o cliente), y borrar cuentas del cliente sin sub-cuentas.
//
// RBAC: owner / admin editan; viewer solo lee; operator no llega (la ruta
// queda detrás de un guard que el router maneja a nivel layout).

import { ApiError } from "@/common/lib/api";
import { useCurrentAccount } from "@/features/accounts/hooks/useCurrentAccount";
import { BookOpen, ChevronRight, Lock, Pencil, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import { toast } from "sonner";
import {
  type ChartNode,
  flattenChartByHierarchy,
  useChart,
  useCreateChartAccount,
  useDeleteChartAccount,
  usePatchChartAccount,
} from "../api/chartApi";
import { AuxiliaryDialog } from "../components/AuxiliaryDialog";

const TYPE_LABEL: Record<string, string> = {
  ASSET: "Activo",
  LIABILITY: "Pasivo",
  EQUITY: "Patrimonio",
  INCOME: "Ingreso",
  EXPENSE: "Gasto",
  COST: "Costo",
};

export function PlanDeCuentasPage() {
  const { currentAccount, currentMembership } = useCurrentAccount();
  const accountId = currentAccount?.id ?? "";
  const role = currentMembership?.role;
  const canEdit = role === "owner" || role === "admin";
  // Sprint 2: viewer/admin/owner pueden ver auxiliar; operator no llega
  // (la ruta entera del módulo está detrás del guard de Settings).
  const canViewAuxiliary = role === "owner" || role === "admin" || role === "viewer";

  const chartQuery = useChart(accountId);
  const flatRows = useMemo(
    () => (chartQuery.data ? flattenChartByHierarchy(chartQuery.data.rows) : []),
    [chartQuery.data],
  );

  const [renameTarget, setRenameTarget] = useState<ChartNode | null>(null);
  const [addUnderParent, setAddUnderParent] = useState<ChartNode | null>(null);
  const [auxiliaryTarget, setAuxiliaryTarget] = useState<ChartNode | null>(null);

  if (chartQuery.isLoading) {
    return (
      <>
        <title>Plan de cuentas · HATO</title>
        <header className="mb-4">
          <h1 className="font-medium text-3xl text-app-ink-1 tracking-tight">Plan de cuentas</h1>
        </header>
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, i) => (
            <div
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton
              key={i}
              className="h-12 animate-pulse rounded-2xl border border-app-border bg-app-surface-2"
            />
          ))}
        </div>
      </>
    );
  }

  return (
    <>
      <title>Plan de cuentas · HATO</title>
      <header className="mb-4">
        <h1 className="font-medium text-3xl text-app-ink-1 tracking-tight">Plan de cuentas</h1>
        <p className="mt-1 text-app-ink-3 text-sm">
          Árbol contable jerárquico de tu finca. Las cuentas del sistema (con candado) las usa HATO
          para generar los asientos automáticos — podés renombrarlas pero no borrarlas. Podés
          agregar sub-cuentas tuyas debajo de cualquier cuenta.
        </p>
        {!canEdit && (
          <div className="mt-3 rounded-2xl border border-app-border bg-app-surface px-4 py-2 text-app-ink-2 text-sm">
            Estás viendo el plan de cuentas en modo solo lectura.
          </div>
        )}
      </header>

      <div className="overflow-x-auto rounded-2xl border border-app-border bg-app-surface">
        <table className="w-full text-sm">
          <thead className="bg-app-surface-2 text-app-ink-3 text-xs">
            <tr>
              <th className="px-4 py-2 text-left">Código</th>
              <th className="px-4 py-2 text-left">Cuenta</th>
              <th className="px-4 py-2 text-left">Tipo</th>
              <th className="px-4 py-2 text-left">Estado</th>
              <th className="px-4 py-2 text-right" />
            </tr>
          </thead>
          <tbody>
            {flatRows.map((row) => (
              <tr key={row.id} className="border-app-border border-t">
                <td className="whitespace-nowrap px-4 py-2 font-mono text-app-ink-2 text-xs">
                  <span style={{ paddingLeft: `${row.depth * 12}px` }}>{row.code}</span>
                </td>
                <td className="px-4 py-2">
                  <div className="flex items-center gap-2">
                    {row.isSystem && (
                      <span
                        title="Cuenta del sistema (HATO)"
                        aria-label="Cuenta del sistema"
                        className="text-app-ink-3"
                      >
                        <Lock className="h-3.5 w-3.5" />
                      </span>
                    )}
                    <span className="text-app-ink-1">{row.name}</span>
                    {row.isContra && (
                      <span className="rounded-full bg-app-surface-2 px-2 py-0.5 text-app-ink-3 text-xs">
                        contra-cuenta
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-2 text-app-ink-2 text-xs">
                  {TYPE_LABEL[row.type] ?? row.type}
                </td>
                <td className="px-4 py-2 text-app-ink-2 text-xs">
                  {row.isActive ? "Activa" : <span className="text-app-ink-3">Inactiva</span>}
                </td>
                <td className="px-4 py-2 text-right">
                  <div className="flex justify-end gap-1">
                    {row.hasAuxiliary && canViewAuxiliary && (
                      <button
                        type="button"
                        onClick={() => setAuxiliaryTarget(row)}
                        title="Ver auxiliar contable"
                        aria-label={`Ver auxiliar contable de ${row.name}`}
                        className="rounded-md p-1.5 text-app-ink-2 hover:bg-app-surface-2"
                      >
                        <BookOpen className="h-4 w-4" />
                      </button>
                    )}
                    {canEdit && (
                      <>
                        {/* v1 (Douglas 2026-06): agregar cuentas deshabilitado en la UI.
                            Backend intacto. Revertir = quitar disabled + el <span> wrapper. */}
                        <span
                          title="Por ahora no se pueden agregar cuentas — pedí al administrador habilitarlo."
                          className="inline-flex cursor-not-allowed"
                        >
                          <button
                            type="button"
                            disabled
                            onClick={() => setAddUnderParent(row)}
                            aria-label={`Agregar sub-cuenta debajo de ${row.name}`}
                            className="pointer-events-none rounded-md p-1.5 text-app-ink-2 opacity-40"
                          >
                            <Plus className="h-4 w-4" />
                          </button>
                        </span>
                        {/* v1 (Douglas 2026-06): renombrar cuentas deshabilitado en la UI.
                            Backend intacto. Revertir = quitar disabled + el <span> wrapper. */}
                        <span
                          title="Por ahora no se pueden renombrar cuentas — pedí al administrador habilitarlo."
                          className="inline-flex cursor-not-allowed"
                        >
                          <button
                            type="button"
                            disabled
                            onClick={() => setRenameTarget(row)}
                            aria-label={`Renombrar ${row.name}`}
                            className="pointer-events-none rounded-md p-1.5 text-app-ink-2 opacity-40"
                          >
                            <Pencil className="h-4 w-4" />
                          </button>
                        </span>
                        {!row.isSystem && <DeleteButton accountId={accountId} row={row} />}
                      </>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {renameTarget && (
        <RenameDialog
          accountId={accountId}
          row={renameTarget}
          onClose={() => setRenameTarget(null)}
        />
      )}
      {addUnderParent && (
        <CreateChildDialog
          accountId={accountId}
          parent={addUnderParent}
          onClose={() => setAddUnderParent(null)}
        />
      )}
      {auxiliaryTarget && (
        <AuxiliaryDialog
          accountId={accountId}
          chartAccount={auxiliaryTarget}
          onClose={() => setAuxiliaryTarget(null)}
        />
      )}
    </>
  );
}

// ─── Dialogs ─────────────────────────────────────────────────────────────────

function RenameDialog({
  accountId,
  row,
  onClose,
}: {
  accountId: string;
  row: ChartNode;
  onClose: () => void;
}) {
  const [name, setName] = useState(row.name);
  const patch = usePatchChartAccount(accountId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("El nombre es obligatorio");
      return;
    }
    try {
      await patch.mutateAsync({ chartAccountId: row.id, name: trimmed });
      toast.success("Cuenta renombrada");
      onClose();
    } catch {
      toast.error("No se pudo renombrar");
    }
  }

  return (
    <DialogShell title={`Renombrar ${row.code}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="rename-name" className="block font-medium text-app-ink-2 text-sm">
            Nombre
          </label>
          <input
            id="rename-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            className="mt-1 w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base"
          />
        </div>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-2 text-app-ink-2 text-sm hover:bg-app-surface-2"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={patch.isPending}
            className="rounded-md bg-app-ink-1 px-4 py-2 font-medium text-[13px] text-white hover:bg-[#1e211d] disabled:opacity-50"
          >
            {patch.isPending ? "Guardando…" : "Guardar"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

function CreateChildDialog({
  accountId,
  parent,
  onClose,
}: {
  accountId: string;
  parent: ChartNode;
  onClose: () => void;
}) {
  const [code, setCode] = useState(`${parent.code}.`);
  const [name, setName] = useState("");
  const create = useCreateChartAccount(accountId);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const tc = code.trim();
    const tn = name.trim();
    if (!tc || !tn) {
      toast.error("Código y nombre son obligatorios");
      return;
    }
    try {
      await create.mutateAsync({ parentId: parent.id, code: tc, name: tn });
      toast.success("Sub-cuenta creada");
      onClose();
    } catch (e) {
      if (e instanceof ApiError && e.code === "DUPLICATE_CODE") {
        toast.error("Ya existe una cuenta con ese código");
      } else {
        toast.error("No se pudo crear la sub-cuenta");
      }
    }
  }

  return (
    <DialogShell title={`Agregar sub-cuenta de ${parent.code} ${parent.name}`} onClose={onClose}>
      <form onSubmit={handleSubmit} className="flex flex-col gap-4">
        <div>
          <label htmlFor="create-code" className="block font-medium text-app-ink-2 text-sm">
            Código
          </label>
          <input
            id="create-code"
            type="text"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            maxLength={50}
            className="mt-1 w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 font-mono text-app-ink-1 text-base"
          />
          <p className="mt-1 text-app-ink-3 text-xs">
            Por convención, las sub-cuentas extienden el código del padre (ej. {parent.code}.1).
          </p>
        </div>
        <div>
          <label htmlFor="create-name" className="block font-medium text-app-ink-2 text-sm">
            Nombre
          </label>
          <input
            id="create-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            maxLength={200}
            className="mt-1 w-full rounded-lg border border-app-border bg-app-surface px-3 py-2 text-app-ink-1 text-base"
          />
        </div>
        <p className="text-app-ink-3 text-xs">
          La sub-cuenta hereda el tipo del padre ({TYPE_LABEL[parent.type] ?? parent.type}).
        </p>
        <div className="flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-md px-3 py-2 text-app-ink-2 text-sm hover:bg-app-surface-2"
          >
            Cancelar
          </button>
          <button
            type="submit"
            disabled={create.isPending}
            className="rounded-md bg-app-ink-1 px-4 py-2 font-medium text-[13px] text-white hover:bg-[#1e211d] disabled:opacity-50"
          >
            {create.isPending ? "Creando…" : "Crear sub-cuenta"}
          </button>
        </div>
      </form>
    </DialogShell>
  );
}

function DeleteButton({ accountId, row }: { accountId: string; row: ChartNode }) {
  const del = useDeleteChartAccount(accountId);
  const [confirming, setConfirming] = useState(false);

  if (confirming) {
    return (
      <div className="flex items-center gap-1">
        <span className="text-app-ink-2 text-xs">¿Borrar {row.code}?</span>
        <button
          type="button"
          onClick={async () => {
            try {
              await del.mutateAsync(row.id);
              toast.success("Cuenta borrada");
            } catch (e) {
              if (e instanceof ApiError && e.code === "ACCOUNT_HAS_CHILDREN") {
                toast.error("No se puede borrar: tiene sub-cuentas");
              } else if (e instanceof ApiError && e.code === "SYSTEM_ACCOUNT_PROTECTED") {
                toast.error("No se puede borrar una cuenta del sistema");
              } else {
                toast.error("No se pudo borrar");
              }
            } finally {
              setConfirming(false);
            }
          }}
          disabled={del.isPending}
          className="rounded-md bg-rose-600 px-2 py-1 font-medium text-white text-xs hover:bg-rose-700 disabled:opacity-50"
        >
          Sí
        </button>
        <button
          type="button"
          onClick={() => setConfirming(false)}
          className="rounded-md px-2 py-1 text-app-ink-2 text-xs hover:bg-app-surface-2"
        >
          No
        </button>
      </div>
    );
  }
  return (
    // v1 (Douglas 2026-06): eliminar cuentas deshabilitado en la UI. Backend
    // intacto. Revertir = quitar disabled + el <span> wrapper.
    <span
      title="Por ahora no se pueden eliminar cuentas — pedí al administrador habilitarlo."
      className="inline-flex cursor-not-allowed"
    >
      <button
        type="button"
        disabled
        onClick={() => setConfirming(true)}
        aria-label={`Borrar ${row.name}`}
        className="pointer-events-none rounded-md p-1.5 text-rose-600 opacity-40"
      >
        <Trash2 className="h-4 w-4" />
      </button>
    </span>
  );
}

function DialogShell({
  title,
  children,
  onClose,
}: {
  title: string;
  children: React.ReactNode;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-4"
      onClick={onClose}
      onKeyDown={(e) => {
        if (e.key === "Escape") onClose();
      }}
      role="presentation"
    >
      <div
        className="w-full max-w-md rounded-2xl bg-app-surface p-5 shadow-xl"
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => e.stopPropagation()}
        // biome-ignore lint/a11y/useSemanticElements: el <dialog> HTML5 tiene su propio modelo de show()/close() incompatible con mount/unmount condicional. Migración a shadcn Dialog es scope futuro.
        role="dialog"
        aria-modal="true"
        aria-label={title}
      >
        <header className="mb-4 flex items-start justify-between">
          <h2 className="font-semibold text-app-ink-1 text-base">{title}</h2>
          <button
            type="button"
            onClick={onClose}
            className="rounded-md p-1 text-app-ink-3 hover:bg-app-surface-2"
            aria-label="Cerrar"
          >
            <ChevronRight className="h-4 w-4 rotate-45" />
          </button>
        </header>
        {children}
      </div>
    </div>
  );
}
